import { UserHelper } from './user-helper.model';
import { Operations } from './operations.model';
import { UserDetails } from 'src/app/shared/models/user-details.model';
import { UserType } from 'src/app/shared/enums/user-type.enum';
import { SettingConfigurations } from './setting-configurations.model';

export class UserRights {

    constructor(
        private userDetails: UserDetails,
        private userHelper: UserHelper,
        private config: SettingConfigurations) {
    }

    // eolConfig key UseAccountManagement
    private _canAccessAccountsManagement = true;

    // configKey _useResultsV2
    private _canAccessResults: boolean = this.config.useResultsV2 && this.canResultViewAccounts();

    // eolConfig key UseOrderingServices && CanPlaceOrder
    private _canAccessOrderingService: boolean = this.isOperationAvailable(Operations.CanPlaceOrder);

    // configKey UseResultsConfiguration && IsInternalUser
    private _canAccessResultsConfiguration: boolean = this.config.useResultsConfiguration && this.isInternalUser();

    // IFeatureAccess Reports
    private _canAccessReports = true;
    private _canAccessImpersonation: boolean = this.isOperationAvailable(Operations.CanImpersonate);
    private _canAccessDisclaimer: boolean = this.isInternalUser() || this.isClientAdmin();

    // configKey UseDocumentSharing
    private _canAccessDocuments: boolean = this.config.useDocumentSharing;
    private _canCreateUser: boolean = this.isClientAdmin();
    private _canCreateAdmin: boolean = this.isInternalUser();
    private _canManageUsers: boolean = this.isClientAdmin();
    private _canManageAdmins: boolean = this.isInternalUser();
    private _canViewDisclaimer: boolean = this.isInternalUser() || this.isClientAdmin();
    private _canViewClients: boolean = this.isInternalUser();
    private _canViewInvoices: boolean = this.isOperationAvailable(Operations.CanViewInvoices);
    private _canEditOwnDetails: boolean = !this.isInternalUser();
    // configKey EnableSelfReport && IFeatureAccess SelfReports
    private _canAccessSelfReport: boolean = this.config.enableSelfReport;
    // configKey EnableOrderResultsView && IFeatureAccess SelfReports
    private _canAccessResultsByOrder: boolean = this.config.enableOrderResultsView;
    // configKey EnableDashboard
    private _canAccessDashBoard: boolean = this.config.enableDashboard;
    private _canManageNewsFeed: boolean = this.isInternalUser() && this.isOperationAvailable(Operations.CanManageNewsFeed);
    // configKey EnableSelfReport && EnableSelfGraph
    private _canAccessSelfGraph: boolean = this.config.enableSelfReport;
    // configKey EnableSelfReport && CanEditLimitsAndAlert
    private _canAccessLimitSets: boolean = this.config.enableSelfReport && this.canEditLimitsAndAlerts();
    private _canManageDefaultReports: boolean = this.isInternalUser() || this.isOperationAvailable(Operations.CanManageDefaultReport);
    // IFeatureAccess Order
    private _canPlaceOrderSubmissionForm: boolean = this.isOperationAvailable(Operations.CanPlaceOrderSubmissionForm);
    // IFeatureAccess Order
    private _canOnlyPlaceOrderSubmissionForm: boolean = this.isOperationAvailable(Operations.CanOnlyPlaceOrderSubmissionForm);
    private _canPlaceOrder: boolean = this.isOperationAvailable(Operations.CanPlaceOrderSubmissionForm) ||
        this.isOperationAvailable(Operations.CanPlaceOrder);
    private _isCustomerUser: boolean = !this.isInternalUser();
    private _canManageAlerts: boolean = this.isOperationAvailable(Operations.CanManageAlerts);
    // IFeatureAccess
    private _canAccessIntercoSubscription = true;
    // userContext cookie consent
    private _isCookieEnabled = false;
    private _canManageOrderingConfigurations: boolean = this.isOperationAvailable(Operations.CanManageOrderingConfigurations);
    private _canConfigureEOL: boolean = this.isOperationAvailable(Operations.CanConfigureEOL);
    private _canViewAssessment: boolean = this.isOperationAvailable(Operations.CanViewAssessment);
    private _canManageAssessment: boolean = this.isOperationAvailable(Operations.CanManageAssessment);
    private _canExpressOrdering: boolean = this.isInternalUser() || this.userHelper.AreAvailableInAnyScope([Operations.CanExpressOrdering]);
    private _canEditSurveyExclusionList: boolean = this.isOperationAvailable(Operations.CanEditSurveyExclusionList);
    private _canPerformSampling: boolean = this.isOperationAvailable(Operations.CanPerformSampling);
    private _canDownloadAR: boolean = this.isOperationAvailable(Operations.CanViewResult) ||
        this.isOperationAvailable(Operations.CanViewResultOnlyIfARRecipient);
    private _canManageLabContacts: boolean = this.isOperationAvailable(Operations.CanManageLabContacts);
    private _canManageAndPlaceOrderForProducts: boolean = this.canManageAndPlaceOrderforProducts();
    // configKey UseResults && this.resultViewAccounts()
    private _canAccessNewResults: boolean = this.config.useResults && this.canResultViewAccounts();
    // CanPlaceGuidedOrder
    private _canPlaceGuidedOrder: boolean = this.isOperationAvailable(Operations.CanPlaceGuidedOrder);

    username: string;
    readonly canAccessAccountsManagement: boolean = this._canAccessAccountsManagement;
    readonly canAccessResults: boolean = this._canAccessResults;
    readonly canAccessOrderingService: boolean = this._canAccessOrderingService;
    readonly canAccessResultsConfiguration: boolean = this._canAccessResultsConfiguration;
    readonly canAccessReports: boolean = this._canAccessReports;
    readonly canAccessImpersonation: boolean = this._canAccessImpersonation;
    readonly canAccessDisclaimer: boolean = this._canAccessDisclaimer;
    readonly canAccessDocuments: boolean = this._canAccessDocuments;
    readonly canCreateUser: boolean = this._canCreateUser;
    readonly canCreateAdmin: boolean = this._canCreateAdmin;
    readonly canManageUsers: boolean = this._canManageUsers;
    readonly canManageAdmins: boolean = this._canManageAdmins;
    readonly canViewDisclaimer: boolean = this._canViewDisclaimer;
    readonly canViewClients: boolean = this._canViewClients;
    readonly canViewInvoices: boolean = this._canViewInvoices;
    readonly canEditOwnDetails: boolean = this._canEditOwnDetails;
    readonly canAccessSelfReport: boolean = this._canAccessSelfReport;
    readonly canAccessDashBoard: boolean = this._canAccessDashBoard;
    readonly canManageNewsFeed: boolean = this._canManageNewsFeed;
    readonly canAccessSelfGraph: boolean = this._canAccessSelfGraph;
    readonly canAccessLimitSets: boolean = this._canAccessLimitSets;
    readonly canManageDefaultReports: boolean = this._canManageDefaultReports;
    readonly canPlaceOrderSubmissionForm: boolean = this._canPlaceOrderSubmissionForm;
    readonly canOnlyPlaceOrderSubmissionForm: boolean = this._canOnlyPlaceOrderSubmissionForm;
    readonly canPlaceOrder: boolean = this._canPlaceOrder;
    readonly isCustomerUser: boolean = this._isCustomerUser;
    readonly canManageAlerts: boolean = this._canManageAlerts;
    readonly canAccessIntercoSubscription: boolean = this._canAccessIntercoSubscription;
    readonly isCookieEnabled: boolean = this._isCookieEnabled;
    readonly canManageOrderingConfigurations: boolean = this._canManageOrderingConfigurations;
    readonly canConfigureEOL: boolean = this._canConfigureEOL;
    readonly canViewAssessment: boolean = this._canViewAssessment;
    readonly canManageAssessment: boolean = this._canManageAssessment;
    readonly canExpressOrdering: boolean = this._canExpressOrdering;
    readonly canEditSurveyExclusionList: boolean = this._canEditSurveyExclusionList;
    readonly canPerformSampling: boolean = this._canPerformSampling;
    readonly canAccessResultsByOrder: boolean = this._canAccessResultsByOrder;
    readonly canDownloadAR: boolean = this._canDownloadAR;
    readonly canManageLabContacts: boolean = this._canManageLabContacts;
    readonly canManageAndPlaceOrderForProducts: boolean = this._canManageAndPlaceOrderForProducts;
    readonly canAccessNewResults: boolean = this._canAccessNewResults;
    readonly canPlaceGuidedOrder: boolean = this._canPlaceGuidedOrder;

    private isOperationAvailable(operation: string): boolean {
        return this.userHelper.IsOperationAvailable(operation);
    }

    private isInternalUser(): boolean {
        return this.userDetails.profile.userType.toString().toLowerCase() !== UserType[0].toString().toLowerCase();
    }

    private isClientAdmin(): boolean {
        return !this.isInternalUser() &&
            this.isOperationAvailable(Operations.CanCreateUser);
    }

    private canEditLimitsAndAlerts(): boolean {
        return this.isInternalUser() ||
            this.userHelper.AreAvailableInAnyScope([Operations.CanViewResult, Operations.CanEditLimitsAndAlerts]) ||
            this.userHelper.AreAvailableInAnyScope([Operations.CanViewResultOnlyIfARRecipient, Operations.CanEditLimitsAndAlerts]);
    }

    private canManageAndPlaceOrderforProducts(): boolean {
        return this.isInternalUser ? this.isOperationAvailable(Operations.CanManageAndPlaceOrderforProducts) :
            this.userHelper.AreAvailableInAnyScope([Operations.CanManageAndPlaceOrderforProducts]);
    }

    private canResultViewAccounts() {
        // configKey ResultViewAccounts
        const configKeyValue: string = this.config.resultViewAccounts;
        if ((configKeyValue === null || configKeyValue === undefined || configKeyValue === 'NULL') &&
            this.isOperationAvailable(Operations.CanViewResult)) {
            return true;
        } else {
            const res: string[] = configKeyValue?.split(',');
            const accountCodes = this.userDetails.profile.scopeOperations.filter(t => t.operationNames.indexOf(Operations.CanViewResult) > -1)
                .map(t => t.accountCode);
            return accountCodes.some(t => res.indexOf(t) > -1);
        }
    }
}
